import {
    useState,
    useRef,
    useEffect,
    useImperativeHandle,
    forwardRef,
    ChangeEvent,
    KeyboardEvent,
    FocusEvent,
} from "react";

import style from "./Input.module.css";

type enterKeyHint = "search" | "done" | "enter" | "go" | "next" | "previous" | "send" | undefined;
type inputMode = "search" | "text" | "none" | "email" | "tel" | "url" | "numeric" | "decimal" | undefined;

interface input {
    placeholder: string;
    readOnly?: boolean;
    disabled?: boolean;
    enterKey?: enterKeyHint;
    inputMode?: inputMode;
    value?: string;
    error?: string;
    onFocus?: (e: FocusEvent) => void;
    onFocusOut?: (e: FocusEvent) => void;
    setValue: (value: string) => void;
    onChange?: (e: ChangeEvent) => void;
    onEnter?: (e: KeyboardEvent) => void;
}

interface LineTextareaProps extends input {
    maxHeight?: number;
}

const BlockInput = forwardRef(
    (
        {
            type = "text",
            placeholder,
            disabled,
            value,
            setValue,
            onChange,
            error,
        }: {
            type?: "text" | "number" | "password";
            placeholder: string;
            disabled?: boolean;
            value?: string;
            setValue: (value: string) => void;
            onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
            error?: string;
        },
        ref
    ) => {
        const input = useRef<HTMLInputElement | null>(null);

        useImperativeHandle(ref, () => ({
            focus: () => input.current?.focus(), // 부모에서 focus 호출 가능
        }));

        return (
            <div>
                <div className={[style["container"], error && style["container--error"]].join(" ")}>
                    <input
                        ref={input}
                        onChange={(e) => {
                            if (onChange) onChange(e);
                            setValue(e.target.value);
                        }}
                        className={style["input--default"]}
                        type={type}
                        value={value || ""}
                        disabled={disabled ? true : false}
                        placeholder=" "
                        autoComplete="off"
                        autoCapitalize="off"
                        autoCorrect="off"
                        spellCheck="false"
                    />

                    <span className={style["span--input--default"]}>{placeholder}</span>
                </div>

                <p className={[style["text--error"], error && style["text--error--active"]].join(" ")}>{error}</p>
            </div>
        );
    }
);

const LineTextarea = forwardRef(
    (
        {
            maxHeight,
            placeholder,
            readOnly = false,
            disabled = false,
            enterKey = "enter",
            value = "",
            error = "",
            onFocus,
            onFocusOut,
            setValue,
            onChange,
        }: LineTextareaProps,
        ref
    ) => {
        const input = useRef<HTMLTextAreaElement | null>(null);
        const container = useRef<HTMLDivElement | null>(null);

        useEffect(() => {
            if (maxHeight) {
                input.current!.style.maxHeight = `${maxHeight}px`;
            }
        }, [maxHeight]);

        useImperativeHandle(ref, () => ({
            focus: () => input.current?.focus(), // 부모에서 focus 호출 가능
            blur: () => input.current?.blur(),
            offsetTop: container.current?.offsetTop,
            clientHeight: container.current?.clientHeight,
        }));

        // 자동 높이 조절 함수
        const adjustHeight = () => {
            if (input.current) {
                input.current.style.height = "0px";
                input.current.style.height = `${input.current.scrollHeight + 23}px`; // 내용에 맞게 조절
            }
        };

        // 값이 변경될 때마다 높이 조정
        useEffect(() => {
            adjustHeight();
        }, [value]);

        const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
            if (onChange) onChange(e);
            setValue(e.target.value);
        };

        return (
            <div ref={container}>
                <div className={[style["container"], error && style["container--error"]].join(" ")}>
                    <textarea
                        rows={1}
                        ref={input}
                        onChange={handleChange}
                        onFocus={onFocus}
                        onBlurCapture={onFocusOut}
                        value={value || ""}
                        className={style["textarea--default"]}
                        readOnly={readOnly}
                        disabled={disabled}
                        enterKeyHint={enterKey}
                        placeholder=" "
                        autoComplete="off"
                        autoCapitalize="off"
                        autoCorrect="off"
                        spellCheck="false"
                    />
                    <span className={style["span--textarea--default"]}>{placeholder}</span>
                </div>

                <p className={[style["text--error"], error && style["text--error--active"]].join(" ")}>{error}</p>
            </div>
        );
    }
);

export { BlockInput, LineTextarea };
